import { QueryKey, useQuery } from '@tanstack/react-query'
import { useConnection } from 'hooks/useConnection'
import { useMemo } from 'react'
import { IslandApiDataResponse, IslandApiResponse } from 'state/data/slice'

import type { BackendQueryOptions } from './api'
import { fetchBackendJson } from './api'

type IslandQueryOptions<TData, TQueryFnData = TData, TError = Error> = BackendQueryOptions<TData, TQueryFnData, TError>

export type IslandsQueryParams = {
  orderBy?: string
  orderDirection?: string
  limit?: number
  offset?: number
  search?: string
  chainId?: number
  rewardVault?: boolean
  sweetened?: boolean
  volatile?: boolean
  minimumTvl?: number
  user?: string
  managerTreasury?: string
  withBaults?: boolean
  smartFilters?: boolean
  managed?: boolean
}

type NormalizedIslandsParams = {
  orderBy: string
  orderDirection: string
  limit: number
  offset: number
  search: string
  chainId?: number
  rewardVault?: boolean
  sweetened?: boolean
  volatile?: boolean
  minimumTvl?: number
  user?: string
  managerTreasury?: string
  withBaults?: boolean
  smartFilters?: boolean
  managed?: boolean
}

const defaultFetchInit: RequestInit = {
  method: 'GET',
  headers: { Accept: 'application/json', 'Accept-Language': 'en-US,en;q=0.9' },
  credentials: 'omit',
}

const islandKeyBase = (chainId: number) => ['islands', chainId] as const

const serializeLegacyIslandsParams = (params: NormalizedIslandsParams) => {
  const filtered = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''))
  return JSON.stringify(filtered)
}

export const islandQueryKeys = {
  all: (chainId: number) => islandKeyBase(chainId),
  list: (chainId: number, params: NormalizedIslandsParams) =>
    [...islandKeyBase(chainId), 'list', serializeLegacyIslandsParams(params)] as const,
  island: (chainId: number, id: string) => [...islandKeyBase(chainId), 'byId', id] as const,
  userTotalBalance: (chainId: number, address: string) => [...islandKeyBase(chainId), 'user-balance', address] as const,
  tvl: (chainId: number) => [...islandKeyBase(chainId), 'tvl'] as const,
}

const normalizeIslandsParams = (params: IslandsQueryParams): NormalizedIslandsParams => {
  const {
    orderBy = 'totalTvl',
    orderDirection = 'desc',
    limit = 20,
    offset = 0,
    search = '',
    chainId,
    rewardVault,
    sweetened,
    volatile,
    minimumTvl,
    user,
    managerTreasury,
    withBaults,
    smartFilters,
    managed,
  } = params

  const trimmedSearch = search?.trim?.() ?? ''
  const normalizedSearch = trimmedSearch.length > 0 ? trimmedSearch : ''

  return {
    orderBy,
    orderDirection,
    limit,
    offset,
    search: normalizedSearch,
    chainId,
    rewardVault,
    sweetened,
    volatile,
    minimumTvl,
    user: user?.toLowerCase(),
    managerTreasury: managerTreasury?.toLowerCase(),
    withBaults,
    smartFilters,
    managed,
  }
}

const buildIslandsSearchParams = (params: NormalizedIslandsParams) => {
  const queryParams = new URLSearchParams({
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
    limit: params.limit.toString(),
    offset: params.offset.toString(),
  })

  if (params.search) queryParams.append('search', params.search)
  if (params.chainId !== undefined) queryParams.append('chainId', params.chainId.toString())
  if (params.rewardVault !== undefined) queryParams.append('rewardVault', params.rewardVault.toString())
  if (params.sweetened !== undefined) queryParams.append('sweetened', params.sweetened.toString())
  if (params.volatile !== undefined) queryParams.append('volatile', params.volatile.toString())
  if (params.minimumTvl !== undefined) queryParams.append('minimumTvl', params.minimumTvl.toString())
  if (params.user) queryParams.append('user', params.user)
  if (params.managerTreasury) queryParams.append('managerTreasury', params.managerTreasury)
  if (params.withBaults !== undefined) queryParams.append('withBaults', params.withBaults.toString())
  if (params.smartFilters !== undefined) queryParams.append('smartFilters', params.smartFilters.toString())
  if (params.managed !== undefined) queryParams.append('managed', params.managed.toString())

  return queryParams.toString()
}

const fetchJson = async <T = any>(path: string, signal?: AbortSignal): Promise<T> => {
  return fetchBackendJson<T>(path, { ...defaultFetchInit, signal })
}

const fetchIslands = async (
  params: NormalizedIslandsParams,
  signal?: AbortSignal
): Promise<IslandApiDataResponse & { meta: { timestamp: number } }> => {
  const searchParams = buildIslandsSearchParams(params)
  const raw = await fetchJson<any>(`/vaults?${searchParams}`, signal)
  const data = Array.isArray(raw) ? raw : raw.data || []
  const count = typeof raw.count === 'number' ? raw.count : data.length
  const smartFiltersUsed = raw?.smartFiltersUsed as boolean | undefined

  return {
    data,
    count,
    smartFiltersUsed,
    meta: { timestamp: Date.now() },
  }
}

const fetchIslandById = async (id: string, chainId: number, signal?: AbortSignal): Promise<IslandApiResponse> => {
  return fetchJson<IslandApiResponse>(`/vaults/${id}?allUsersPage=1&chainId=${chainId}`, signal)
}

const fetchUserTotalBalance = async (address: string, chainId: number, signal?: AbortSignal): Promise<number> => {
  const raw = await fetchJson<any>(`/vaults?user=${address}&chainId=${chainId}`, signal)
  const data = Array.isArray(raw) ? raw : raw?.data || []

  return (data as any[]).reduce((totalBalanceUSD: number, island: any) => {
    const { balanceUSD = 0 } = island
    return totalBalanceUSD + balanceUSD
  }, 0)
}

const fetchIslandsTvl = async (chainId: number, signal?: AbortSignal): Promise<number> => {
  const raw = await fetchJson<{ totalTvl?: number }>(`/vaults/tvl?chainId=${chainId}`, signal)
  const totalTvl = Number(raw?.totalTvl ?? 0)
  return Number.isFinite(totalTvl) ? totalTvl : 0
}

export const useIslandsQuery = (
  params: IslandsQueryParams,
  options?: IslandQueryOptions<IslandApiDataResponse & { meta: { timestamp: number } }>
) => {
  const { chainId } = useConnection()
  const normalizedParams = useMemo(
    () => normalizeIslandsParams({ ...params, chainId: params.chainId ?? chainId }),
    [
      params.orderBy,
      params.orderDirection,
      params.limit,
      params.offset,
      params.search,
      params.rewardVault,
      params.sweetened,
      params.volatile,
      params.minimumTvl,
      params.user,
      params.managerTreasury,
      params.withBaults,
      params.smartFilters,
      params.managed,
      params.chainId,
      chainId,
    ]
  )

  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = true,
    refetchInterval = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedParams.chainId)

  return useQuery<IslandApiDataResponse & { meta: { timestamp: number } }, Error>({
    queryKey: (queryKey ?? islandQueryKeys.list(chainId, normalizedParams)) as QueryKey,
    queryFn: ({ signal }) => fetchIslands(normalizedParams, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchInterval,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  })
}

export const useIslandById = (id: string | null | undefined, options?: IslandQueryOptions<IslandApiResponse>) => {
  const { chainId } = useConnection()
  const normalizedId = (id ?? '').toLowerCase()
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchInterval = 5 * 60 * 1000,
    ...queryOptions
  } = options ?? {}

  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedId)

  return useQuery<IslandApiResponse, Error>({
    queryKey: (queryKey ?? islandQueryKeys.island(chainId, normalizedId)) as QueryKey,
    queryFn: ({ signal }) => fetchIslandById(normalizedId, chainId, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchInterval,
    staleTime: 2 * 60 * 1000,
    ...queryOptions,
  })
}

export const useUserTotalBalance = (address: string | null | undefined, options?: IslandQueryOptions<number>) => {
  const { chainId } = useConnection()
  const normalizedAddress = (address ?? '').toLowerCase()
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchInterval = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedAddress)

  return useQuery<number, Error>({
    queryKey: (queryKey ?? islandQueryKeys.userTotalBalance(chainId, normalizedAddress)) as QueryKey,
    queryFn: ({ signal }) => fetchUserTotalBalance(normalizedAddress, chainId, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchInterval,
    staleTime: 30 * 1000,
    ...queryOptions,
  })
}

export const useIslandsTVL = () => {
  const { chainId } = useConnection()

  return useQuery<number, Error>({
    queryKey: islandQueryKeys.tvl(chainId),
    queryFn: ({ signal }) => fetchIslandsTvl(chainId as number, signal),
    enabled: Boolean(chainId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    staleTime: 5 * 60 * 1000,
  })
}

export { normalizeIslandsParams }
